import { useState } from 'react';
import { parseWeb3Response, submitWeb3Form } from '../utils/web3forms';
import { buildBookingMessage } from '../utils/bookingForm';

function applyContactFields(formData) {
  const contactName = [
    formData.get('title'),
    formData.get('firstName'),
    formData.get('middleName'),
    formData.get('lastName'),
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (contactName) formData.set('name', contactName);
  if (!formData.get('email') && formData.get('contactEmail')) {
    formData.set('email', formData.get('contactEmail'));
  }
  if (!formData.get('phone') && formData.get('contactPhone')) {
    formData.set('phone', formData.get('contactPhone'));
  }
}

/**
 * Booking checkout — contact step emails via Web3Forms; final confirm emails again then technical-issue screen.
 */
export function useBookingCheckout({ subject, bookingLines = [], extraLines = {} }) {
  const [phase, setPhase] = useState('form'); // form | contact-sending | loading | issue
  const [contactSent, setContactSent] = useState(false);

  const buildMessage = (formData, stepNote) =>
    buildBookingMessage(formData, bookingLines, {
      ...extraLines,
      bookingStep: stepNote,
      ticketTier: formData.get('ticketTier'),
      webCheckIn: formData.get('webCheckIn'),
      cancellationProtection: formData.get('cancellationProtection'),
    });

  const sendForm = async (formData, subjectLine, stepNote) => {
    if (subjectLine) formData.set('subject', subjectLine);
    applyContactFields(formData);
    formData.set('message', buildMessage(formData, stepNote));
    const response = await submitWeb3Form(formData);
    return parseWeb3Response(response);
  };

  const submitContactStep = async (form) => {
    const email = form.contactEmail?.value?.trim();
    const phone = form.contactPhone?.value?.trim();
    if (!email || !phone) {
      form.reportValidity();
      return false;
    }

    setPhase('contact-sending');
    const formData = new FormData(form);
    try {
      await sendForm(
        formData,
        subject ? `${subject} — Contact details` : 'Booking — Contact details',
        'Step 2: Customer contact (booking in progress)'
      );
      setContactSent(true);
    } catch {
      setContactSent(true);
    } finally {
      setPhase('form');
    }
    return true;
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    setPhase('loading');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      await sendForm(
        formData,
        subject ? `${subject} — Booking confirmation request` : 'Booking confirmation request',
        'Step 4: Final booking submission (awaiting agent confirmation)'
      );
    } catch {
      /* Still show technical issue page */
    }

    await new Promise((r) => setTimeout(r, 1800));
    setPhase('issue');
  };

  return {
    phase,
    contactSent,
    submitContactStep,
    handleFinalSubmit,
    isLoading: phase === 'loading',
    isContactSending: phase === 'contact-sending',
    showIssue: phase === 'issue',
  };
};
